import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { User, Unit, Event, Group, Company, CompanyUser } from '../types';
import { getEventDescription, getEventUserDisplay } from './eventUtils';
import { formatLockMode } from './lockModeUtils';

/** A company user enriched with its parent company for flat export. */
export interface CompanyUserExportRow extends CompanyUser {
  company_name: string;
  company_code: string;
}

const safeDate = (value?: number | string | null) => {
  if (value == null) return 'N/A';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'N/A' : format(d, 'dd/MM/yyyy HH:mm:ss', { locale: enUS });
};

export const exportToExcel = (data: any[], filename: string, sheetName: string = 'Data') => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
  // Adjust column widths
  const colWidths = Object.keys(data[0] || {}).map(() => ({ wch: 20 }));
  worksheet['!cols'] = colWidths;
  
  XLSX.writeFile(workbook, `${filename}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.xlsx`);
};

export const exportUserData = (user: User) => {
  const userData = [{
    'ID': user.id,
    'Name': user.name,
    'Email': user.email,
    'Export date': format(new Date(), 'dd/MM/yyyy HH:mm:ss', { locale: enUS })
  }];
  
  exportToExcel(userData, 'user_data', 'User');
};

const buildUnitRow = (unit: Unit) => ({
  'ID': unit.id,
  'Name': unit.name,
  'MAC address': unit.mac_address || 'N/A',
  'Description': unit.description || 'N/A',
  'Group': unit.group_name || 'No group',
  'Battery (%)': unit.battery ?? 'N/A',
  'Temperature (°C)': unit.temperature ?? 'N/A',
  'Container type': unit.container_type || 'N/A',
  'Lock mode': formatLockMode(unit.accelerometer),
  'Owner ID': unit.owner_id,
  'Shared users': unit.sharedWith?.map(u => u.email).join(', ') || 'None',
  'Created date': safeDate(unit.created_at),
  'Last updated': safeDate(unit.updated_at),
});

export const exportUnitsData = (units: Unit[]) => {
  exportToExcel(units.map(buildUnitRow), 'units', 'Units');
};


export const exportEventsData = (events: Event[], unitName?: string) => {
  const dataToExport = events.map(event => ({
    'Event ID': event.id,
    'Unit ID': event.unit_id,
    'Event': getEventDescription(event),
    'User': getEventUserDisplay(event),
    'Date and time': format(new Date(event.registered_at), 'dd/MM/yyyy HH:mm', { locale: enUS }),
  }));

  const filename = unitName ? `events-${unitName.replace(/ /g, '_')}` : `events-${new Date().toISOString()}`;
  exportToExcel(dataToExport, filename, 'Events');
};

export const exportGroupsData = (groups: Group[]) => {
  const groupsData = groups.map(group => ({
    'ID': group.id,
    'Name': group.name,
    'Description': group.description || '',
    'Units number': group.units.length,
    'Units': group.units.map(u => u.name).join(', ')
  }));
  
  exportToExcel(groupsData, 'groups', 'Groups');
};

export const exportAllData = (
  user: User,
  units: Unit[],
  events: Event[],
  groups: Group[],
  companies: Company[] = [],
  companyUsers: CompanyUserExportRow[] = []
) => {
  const workbook = XLSX.utils.book_new();
  const unitNameById = new Map(units.map(u => [u.id, u.name]));

  const appendSheet = (name: string, rows: any[]) => {
    // json_to_sheet drops column headers when the array is empty, so seed a
    // placeholder row to keep the sheet readable.
    const data = rows.length > 0 ? rows : [{ 'No data': '—' }];
    const sheet = XLSX.utils.json_to_sheet(data);
    sheet['!cols'] = Object.keys(data[0]).map(() => ({ wch: 22 }));
    XLSX.utils.book_append_sheet(workbook, sheet, name);
  };

  // Summary sheet
  appendSheet('Summary', [{
    'Account': user.name || user.full_name || '',
    'Email': user.email,
    'Total units': units.length,
    'Total events': events.length,
    'Total groups': groups.length,
    'Total companies': companies.length,
    'Total company users': companyUsers.length,
    'Export date': format(new Date(), 'dd/MM/yyyy HH:mm:ss', { locale: enUS }),
  }]);

  // Units sheet (enriched)
  appendSheet('Units', units.map(buildUnitRow));

  // Events sheet (all locks)
  appendSheet('Events', events.map(event => ({
    'ID': event.id,
    'Unit ID': event.unit_id,
    'Unit name': unitNameById.get(event.unit_id) || 'Unknown',
    'Type': event.type,
    'Description': getEventDescription(event),
    'User': getEventUserDisplay(event),
    'Date and time': safeDate(event.registered_at),
  })));

  // Groups sheet
  appendSheet('Groups', groups.map(group => ({
    'ID': group.id,
    'Name': group.name,
    'Description': group.description || '',
    'Owned by me': group.is_own === 1 ? 'Yes' : 'No',
    'Units number': group.units.length,
    'Units': group.units.map(u => u.name).join(', '),
  })));

  // Companies sheet
  appendSheet('Companies', companies.map(company => ({
    'ID': company.id,
    'Code': company.code,
    'Name': company.name,
    'Users': company.user_count ?? 0,
    'Max users': company.max_users ?? 'Unlimited',
    'Login mode': (company.login_mode ?? 'password') === 'code_only' ? 'Code only' : 'Username + password',
    'Status': company.is_active ? 'Active' : 'Inactive',
    'Created': safeDate(company.created_at),
  })));

  // Company users sheet
  appendSheet('Company Users', companyUsers.map(u => ({
    'Company': u.company_name,
    'Company code': u.company_code,
    'User ID': u.id,
    'Username': u.company_username,
    'First name': u.first_name || '',
    'Last name': u.last_name || '',
    'Full name': u.full_name || '',
    'Role': u.company_role,
    'Created': safeDate(u.created_at),
  })));

  XLSX.writeFile(workbook, `complete_data_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.xlsx`);
};